// src/pages/Properties/PropertyDetailPage.tsx
import {useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  MapPin,
  Calendar,
  DollarSign,
  Home,
  Building,
  Eye,
  Image,
  CheckCircle,
  XCircle,
  Clock,
} from "lucide-react";
import {PageHeader} from "../../components/common";
import {Button, Card, Badge, Modal} from "../../components/ui";
import {useProperty} from "../../hooks/useProperties";
import {useModal} from "../../hooks/common/useModal";
import {formatCurrency, formatDate, formatNumber} from "../../utils/formatters";
import {ROUTES} from "../../utils/constants";
import PropertyPhotoGallery from "./components/PropertyPhotoGallery";

const PropertyDetailPage: React.FC = () => {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const propertyId = id ? parseInt(id, 10) : undefined;

  // Modal hooks
  const photoGalleryModal = useModal("photo-gallery");

  // Data fetching
  const {property, isLoading, error} = useProperty(propertyId);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Property Details"
          breadcrumbs={[
            {label: "Dashboard", path: "/"},
            {label: "Properties", path: "/properties"},
            {label: "Loading..."},
          ]}
        />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Property Details"
          breadcrumbs={[
            {label: "Dashboard", path: "/"},
            {label: "Properties", path: "/properties"},
            {label: "Error"},
          ]}
        />
        <Card className="p-8 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Property Not Found
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            The property you're looking for doesn't exist or has been removed.
          </p>
          <Button
            onClick={() => navigate(ROUTES.PROPERTIES)}
            variant="outline"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Properties
          </Button>
        </Card>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge variant="success">Active</Badge>;
      case "sold":
        return <Badge variant="gray">Sold</Badge>;
      case "rented":
        return <Badge variant="info">Rented</Badge>;
      case "inactive":
        return <Badge variant="danger">Inactive</Badge>;
      default:
        return <Badge variant="gray">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={property.titleEng}
        breadcrumbs={[
          {label: "Dashboard", path: "/"},
          {label: "Properties", path: "/properties"},
          {label: property.titleEng},
        ]}
        action={{
          label: "Edit Property",
          onClick: () => navigate(ROUTES.PROPERTY_EDIT(property.id)),
          icon: <Edit className="w-4 h-4 mr-2" />,
        }}
        backAction={{
          label: "Back to Properties",
          onClick: () => navigate(ROUTES.PROPERTIES),
          icon: <ArrowLeft className="w-4 h-4 mr-2" />,
        }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Property Images */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Photos
              </h2>
              <Button
                onClick={() => photoGalleryModal.open()}
                variant="outline"
                size="sm"
              >
                <Image className="w-4 h-4 mr-2" />
                View All ({property.photoCount || 0})
              </Button>
            </div>
            
            {property.primaryPhoto?.photoUrl ? (
              <div className="aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                <img
                  src={property.primaryPhoto.photoUrl}
                  alt={property.titleEng}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Image className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">No photos available</p>
                </div>
              </div>
            )}
          </Card>

          {/* Property Description */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Description
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                  English
                </h3>
                <div 
                  className="prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{__html: property.descriptionEng}}
                />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                  Myanmar
                </h3>
                <div 
                  className="prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{__html: property.descriptionMm}}
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Property Info Card */}
          <Card className="p-6">
            <div className="space-y-4">
              {/* Price */}
              <div className="text-center border-b border-gray-200 dark:border-gray-700 pb-4">
                <div className="text-3xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(Number(property.price) || 0)}
                </div>
                <Badge
                  variant={property.listingType === "sale" ? "success" : "info"}
                  className="mt-2"
                >
                  For {property.listingType === "sale" ? "Sale" : "Rent"}
                </Badge>
              </div>

              {/* Property Details */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">ID</span>
                  <span className="font-mono text-sm text-primary-600 dark:text-primary-400">
                    {property.uniqueId}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
                  {getStatusBadge(property.status)}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Type</span>
                  <div className="flex items-center">
                    {property.propertyType?.icon && (
                      <span className="mr-2">{property.propertyType.icon}</span>
                    )}
                    <span className="text-sm">{property.propertyType?.nameEng || 'N/A'}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Location</span>
                  <div className="text-right">
                    <div className="flex items-center text-sm">
                      <MapPin className="w-3 h-3 mr-1" />
                      {property.township?.nameEng || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">{property.region?.nameEng || 'N/A'}</div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Bedrooms</span>
                  <div className="flex items-center">
                    <Home className="w-4 h-4 mr-1" />
                    <span>{property.bedrooms || 0}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Bathrooms</span>
                  <div className="flex items-center">
                    <Building className="w-4 h-4 mr-1" />
                    <span>{property.bathrooms || 0}</span>
                  </div>
                </div>

                {property.sizeArea && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Size</span>
                    <span className="text-sm">{property.sizeArea}</span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Views</span>
                  <div className="flex items-center">
                    <Eye className="w-4 h-4 mr-1" />
                    <span>{formatNumber(property.reads || 0)}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Created</span>
                  <div className="flex items-center text-sm">
                    <Calendar className="w-3 h-3 mr-1" />
                    {formatDate(property.createdAt, "short")}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Actions Card */}
          <Card className="p-4">
            <div className="space-y-3">
              <Button
                onClick={() => navigate(ROUTES.PROPERTY_EDIT(property.id))}
                className="w-full"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Property
              </Button>
              
              {/* <Button
                onClick={() => photoGalleryModal.open()}
                variant="outline"
                className="w-full"
              >
                <Image className="w-4 h-4 mr-2" />
                View Photos
              </Button> */}
              
              <Button
                onClick={() => {
                  if (confirm("Are you sure you want to delete this property?")) {
                    // TODO: Implement delete functionality
                    console.log("Delete property:", property.id);
                  }
                }}
                variant="outline"
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Property
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Photo Gallery Modal */}
      <Modal
        isOpen={photoGalleryModal.isOpen}
        onClose={photoGalleryModal.close}
        title={`Photo Gallery - ${property.titleEng}`}
        size="xl"
      >
        <PropertyPhotoGallery
          property={property}
          onClose={photoGalleryModal.close}
        />
      </Modal>
    </div>
  );
};

export default PropertyDetailPage;